import React from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import type { AdmissionFieldOption } from '../../../types/admissions';
import { assignKeys } from '../../../utils/fieldKey';

/**
 * The choices of a dropdown / multi-select form field — one input used by
 * every form editor. Type a choice and press Enter, or paste several
 * separated by commas; duplicates are dropped. A choice that already
 * existed keeps its stored value (saved answers point at it); new ones get
 * a value from their label.
 */

interface Props {
  options: AdmissionFieldOption[] | undefined;
  onChange: (options: AdmissionFieldOption[]) => void;
  disabled?: boolean;
}

const FieldOptionsInput: React.FC<Props> = ({ options = [], onChange, disabled }) => (
  <Autocomplete
    multiple
    freeSolo
    size="small"
    options={[]}
    disabled={disabled}
    value={options.map(o => o.label)}
    onChange={(_, next) => {
      const seen = new Set<string>();
      const labels = (next as string[])
        .flatMap(v => v.split(','))
        .map(v => v.trim())
        .filter(label => {
          const lower = label.toLowerCase();
          if (!label || seen.has(lower)) return false;
          seen.add(lower);
          return true;
        });
      const byLabel = new Map(options.map(o => [o.label.toLowerCase(), o.value]));
      const keyed = assignKeys(labels.map(label => ({ key: byLabel.get(label.toLowerCase()) ?? '', label })), o => o.label, 'option');
      onChange(keyed.map(o => ({ value: o.key, label: o.label })));
    }}
    renderTags={(value, getTagProps) =>
      value.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return <Chip key={key} {...tagProps} size="small" label={option} />;
      })
    }
    renderInput={(params) => (
      <TextField
        {...params}
        size="small"
        label="Options *"
        placeholder={options.length === 0 ? 'Type a choice and press Enter…' : ''}
        error={options.length === 0}
        helperText={options.length === 0
          ? 'Add the choices parents pick from.'
          : 'Press Enter after each choice. You can paste several separated by commas.'}
      />
    )}
  />
);

export default FieldOptionsInput;
